/**
 * Base → Create: pick what to create. The entry point for every Create type —
 * Translate (a document itself, layout kept) and Q&A (answer a questionnaire
 * from a collection) — plus a placeholder for what comes next.
 *
 * Every role (ADR-0018); each type's page and the backend decide which
 * collections a person may use.
 */

import { Badge, Card, Group, SimpleGrid, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconLanguage, IconListCheck, type Icon } from "@tabler/icons-react";
import { Link as RouterLink } from "react-router-dom";

interface CreateType {
  key: string;
  title: string;
  description: string;
  to: string | null;
  icon: Icon;
}

const TYPES: CreateType[] = [
  {
    key: "translate",
    title: "Translate",
    description:
      "Translate a document from a collection into another language. A PDF stays a PDF, a Word file stays a Word file — the layout is kept. The translation is added to the same collection.",
    to: "/solutions/create/translate",
    icon: IconLanguage,
  },
  {
    key: "qa",
    title: "Q&A",
    description:
      "Answer a questionnaire from a collection's documents. Every answer cites its sources.",
    to: "/solutions/create/qa",
    icon: IconListCheck,
  },
];

function TypeCard({ type }: { type: CreateType }) {
  const TypeIcon = type.icon;
  const live = type.to !== null;
  const body = (
    <Stack gap="xs">
      <Group justify="space-between" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon variant="light" size="lg" radius="md" color={live ? "blue" : "gray"}>
            <TypeIcon size={20} />
          </ThemeIcon>
          <Text fw={600}>{type.title}</Text>
        </Group>
        {!live && (
          <Badge variant="light" color="gray" size="sm">
            Soon
          </Badge>
        )}
      </Group>
      <Text size="sm" c="dimmed">
        {type.description}
      </Text>
    </Stack>
  );
  if (!live || !type.to) {
    return (
      <Card withBorder radius="md" p="lg" aria-disabled="true" data-testid={`create-${type.key}`}>
        {body}
      </Card>
    );
  }
  return (
    <Card
      withBorder
      radius="md"
      p="lg"
      component={RouterLink}
      to={type.to}
      aria-label={`Create: ${type.title}`}
      data-testid={`create-${type.key}`}
      style={{ cursor: "pointer" }}
    >
      {body}
    </Card>
  );
}

export function CreatePicker() {
  return (
    <Stack gap="lg">
      <div>
        <Title order={2}>Create</Title>
        <Text c="dimmed" size="sm">
          Produce new material from your documents. Choose what to create.
        </Text>
      </div>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {TYPES.map((t) => (
          <TypeCard key={t.key} type={t} />
        ))}
      </SimpleGrid>
    </Stack>
  );
}
